// «Фильтры» списка на телефоне (046: T002; contracts/ui.md, «Общее»; ux.md «Табеля», § 15; Modal
// README): до 640 px условия экрана уходят из панели в окно снизу, а включённые видны лентой чипов.
// Компонент только раскладывает: условия, их меню, тексты и состояние остаются у экрана (research
// Р3, Р4), ряды окна — те же кнопки-меню, что на панели шире 640 px (MUST ux § 15: те же действия,
// в том же порядке и с теми же текстами).
//
// PhoneFilters — кнопка «Фильтры» с числом N условий, которые сужают список (имя для программ
// чтения — «Фильтры, действует: N»), и окно Modal «Фильтры» (класс kp-filter-sheet): тело — ряды
// экрана, подвал — сброс с текстом экрана слева (только при N > 0) и одна «Готово» справа (README
// Modal: всё применяется сразу — одна кнопка «Готово», без «Отмены»). Окно закрывают «Готово», Esc
// и затемнение; фокус — на элемент, который открыл окно (MUST ux § 1 «Слои и Esc»): кнопка
// «Фильтры» или чип; чипа уже нет (условие сняли в окне) или касание не дало фокуса — «Фильтры».
// PhoneChips — группа «Включённые фильтры» одной строкой с прокруткой вбок, в конце — сброс (если
// сбрасывать есть что — как шире 640 px); нет чипов — ничего нет. ✕ у чипа — фокус на ✕ соседнего чипа, у последнего — на «Фильтры».
// Лента — отдельный компонент, а не свойство chips у PhoneFilters (contracts/ui.md): кнопка стоит
// в строке поиска, лента — под ней. Связь — triggerRef кнопки и filtersRef ленты; клик по чипу
// (onEdit FilterChip) открывает окно через управляемое open экрана.
// Шире 640 px оба не рисуются: открытое окно закрывается, условия остаются у экрана.
import { Badge, Button, Modal, ModalFooter, cx, useIsPhone } from '@kp/ui';
import { FilterX, ListFilter } from '@kp/ui/icons';
import { Children, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent, ReactNode, RefObject } from 'react';
import './phone-filters.css';

/** Имя кнопки «Фильтры» для программ чтения: «Фильтры» или «Фильтры, действует: N». */
export function phoneFiltersName(count: number): string {
  return count > 0 ? `Фильтры, действует: ${count}` : 'Фильтры';
}

export interface PhoneFiltersProps {
  /** N — условия, которые сужают список (без поиска, сортировки, группировки и «Даты отсчёта»). */
  count: number;
  /** Ряды окна: те же кнопки-меню, что на панели шире 640 px, в том же порядке. */
  children: ReactNode;
  /** Текст сброса экрана: «Сбросить все», «Сбросить фильтры», «Сбросить всё». */
  resetLabel: string;
  /** null — сброса нет (N = 0). */
  onReset: (() => void) | null;
  /** Управляемо: чип открывает окно. Без open — окно своё. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Кнопка «Фильтры»: сюда PhoneChips ставит фокус, когда чипов не осталось. */
  triggerRef?: RefObject<HTMLButtonElement | null>;
  className?: string;
}

export function PhoneFilters({
  count,
  children,
  resetLabel,
  onReset,
  open: openProp,
  onOpenChange,
  triggerRef,
  className,
}: PhoneFiltersProps) {
  const phone = useIsPhone();
  const [ownOpen, setOwnOpen] = useState(false);
  const ownRef = useRef<HTMLButtonElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  const buttonRef = triggerRef ?? ownRef;
  const open = openProp ?? ownOpen;

  // Кто открыл окно: кнопка или чип (onEdit → open экрана). Запоминаем при открытии, пока фокус
  // ещё не ушёл в окно; Modal возвращает фокус туда, а если элемента уже нет — на «Фильтры».
  const openerRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }, [open]);
  const returnRef = useMemo<RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        const opener = openerRef.current;
        return opener?.isConnected ? opener : buttonRef.current;
      },
    }),
    [buttonRef],
  );

  const setOpen = (next: boolean) => {
    setOwnOpen(next);
    onOpenChange?.(next);
  };

  // Шире 640 px окна нет: открытое закрывается, чтобы не встать снова при возврате на телефон.
  // Своё состояние — при отрисовке (смена ступени видна сразу), окно экрана — через onOpenChange.
  const [wasPhone, setWasPhone] = useState(phone);
  if (wasPhone !== phone) {
    setWasPhone(phone);
    if (!phone) setOwnOpen(false);
  }
  useEffect(() => {
    if (!phone && openProp) onOpenChange?.(false);
  }, [phone, openProp, onOpenChange]);

  if (!phone) return null;

  const reset = () => {
    onReset?.();
    // Сброс пропадает из подвала вместе с условиями — фокус на «Готово», а не на окно.
    doneRef.current?.focus();
  };

  return (
    <>
      <Button
        ref={buttonRef}
        icon={ListFilter}
        className={cx('kp-phone-filters-btn', count > 0 && 'kp-on', className)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={phoneFiltersName(count)}
        onClick={() => setOpen(true)}
      >
        Фильтры
        {count > 0 ? (
          <Badge tone="accent" className="kp-phone-filters-n">
            {count}
          </Badge>
        ) : null}
      </Button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Фильтры"
        className="kp-filter-sheet"
        returnFocusRef={returnRef}
        footer={
          <ModalFooter
            cancelLabel={null}
            secondary={
              onReset ? (
                <Button variant="ghost" icon={FilterX} onClick={reset}>
                  {resetLabel}
                </Button>
              ) : null
            }
            primary={
              <Button ref={doneRef} variant="primary" onClick={() => setOpen(false)}>
                Готово
              </Button>
            }
          />
        }
      >
        <div className="kp-filter-rows">{children}</div>
      </Modal>
    </>
  );
}

export interface PhoneChipsProps {
  /** Чипы экрана (FilterChip «Табеля») — массивом или элементами, не фрагментом: по ним видно, есть ли лента. */
  children: ReactNode;
  /** Перед сбросом: итог «Найдено: N · родителей для контекста: M» у протокола. */
  summary?: ReactNode;
  /** Текст сброса экрана — тот же, что в подвале окна. */
  resetLabel: string;
  /**
   * null — сбрасывать нечего (протокол: включена только «Моя зона»; «Контроль»: только «Дата
   * отсчёта»): кнопки нет, как шире 640 px, а не кнопка, которая ничего не делает.
   */
  onReset: (() => void) | null;
  /** Кнопка «Фильтры» (triggerRef у PhoneFilters): фокус, когда чипов не осталось. */
  filtersRef: RefObject<HTMLElement | null>;
}

/** ✕ чипов ленты по порядку. */
function removeButtons(root: HTMLElement | null): HTMLElement[] {
  return Array.from(root?.querySelectorAll<HTMLElement>('.t-chip .t-ibtn') ?? []);
}

export function PhoneChips({
  children,
  summary,
  resetLabel,
  onReset,
  filtersRef,
}: PhoneChipsProps) {
  const phone = useIsPhone();
  const ref = useRef<HTMLDivElement>(null);
  // Нажатый ✕ (или сброс): номер и сколько ✕ было. Фокус переходит, когда ✕ стало меньше:
  // экран мог перерисоваться раньше, чем снял условие (адрес страницы меняется не сразу).
  const pending = useRef<{ index: number; count: number } | null>(null);

  useLayoutEffect(() => {
    const wait = pending.current;
    if (wait === null) return;
    const removes = removeButtons(ref.current);
    if (wait.count > 0 && removes.length >= wait.count) return;
    pending.current = null;
    const next = removes[Math.min(wait.index, removes.length - 1)];
    if (next) next.focus();
    else filtersRef.current?.focus();
  });

  if (!phone || Children.toArray(children).length === 0) return null;

  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target.closest('.t-chip .t-ibtn') : null;
    if (!(target instanceof HTMLElement)) return;
    const removes = removeButtons(ref.current);
    pending.current = { index: removes.indexOf(target), count: removes.length };
  };

  return (
    <div
      ref={ref}
      className="kp-phone-chips"
      role="group"
      aria-label="Включённые фильтры"
      onClickCapture={onClickCapture}
    >
      {children}
      {summary}
      {onReset ? (
        <Button
          size="sm"
          variant="ghost"
          icon={FilterX}
          className="kp-phone-chips-reset"
          onClick={() => {
            pending.current = { index: 0, count: removeButtons(ref.current).length };
            onReset();
          }}
        >
          {resetLabel}
        </Button>
      ) : null}
    </div>
  );
}
